import {
    CommentItem,
    DocumentTypeEnum,
    Expense,
    ExpenseKindEnum,
    ExpenseStatusEnum,
    Hospitality,
    MatchingDataInterface,
    Mileage,
    OperationTypeEnum,
    PerDiem,
    PerDiemDestination,
    SubscriptionTypeEnum,
} from "@finway-group/shared/lib/models"
import { getTableQueryParams } from "../../shared/hooks/table.hooks"

import axios from "axios"

const MODEL = "expenses"

const ExpenseService = {
    createExpense: async (expense: Expense | PerDiem | Mileage | Hospitality) => {
        const { data } = await axios.post(`/expenses`, expense)
        switch (data.kind) {
            case ExpenseKindEnum.HOSPITALITY:
                return new Hospitality(data)
            case ExpenseKindEnum.MILEAGE:
                return new Mileage(data)
            case ExpenseKindEnum.PER_DIEM:
                return new PerDiem(data)
            default:
                return new Expense(data)
        }
    },
    createPreApprovedSubscription: async (expense: Expense) => {
        const { data } = await axios.post(`/expenses/subscription`, expense)
        return new Expense(data)
    },
    createRecurringExpense: async (expense: Expense) => {
        const { data } = await axios.post(`/expenses/recurring`, expense)
        return new Expense(data)
    },
    fetchExpenseCount: async (query = "") => {
        const { data } = await axios.get(`${MODEL}/reports/counts?${query}`)
        return data
    },
    // TODO: V Remove this fn once all expense tables are fetched with fetchAllExpenses() function
    fetchExpenses: async (query = "", sortingCriteria = "", page = 1, limit = 20) => {
        const {
            data: { docs, totalPages, totalDocs },
        } = await axios.get(`${MODEL}/?page=${page}&limit=${limit}${query}${sortingCriteria}`)
        return {
            expenses: docs.map((item: any) => new Expense(item)).filter((expense: Expense) => !expense.deleted),
            totalPages,
            totalDocs,
        }
    },
    fetchAllExpenses: async (queryParams: any, source: any, timeout?: number) => {
        const { data } = await axios.get(
            `/${MODEL}?page=${queryParams.page}&limit=${queryParams.limit}${queryParams.filterQueryString}${queryParams.searchQueryString}${queryParams.sortQueryString}`,
            {
                cancelToken: source.token,
                timeout: timeout,
            }
        )
        return data
    },
    fetchSubscriptions: async (sortingCriteria = "") => {
        const subscriptionFilter = `&kind[eq]=${ExpenseKindEnum.SUBSCRIPTION}&subscriptionType[eq]=${SubscriptionTypeEnum.INITIAL_REQUEST}&status[in]=${ExpenseStatusEnum.PAID},${ExpenseStatusEnum.DONE}`
        const { expenses } = await ExpenseService.fetchExpenses(subscriptionFilter, sortingCriteria, 1, 1000)
        return expenses
    },
    fetchMatchedTransactions: async (expenseId: string) => {
        const { data } = await axios.get(`${MODEL}/${expenseId}/matched-transactions`)
        return data
    },
    fetchOneExpense: async (expenseId: string) => {
        const { data } = await axios.get(`${MODEL}/${expenseId}`)
        return new Expense(data)
    },
    updateExpense: async (id: string, updateExpense: Expense) => {
        const { data } = await axios.put(`/${MODEL}/${id}`, updateExpense)
        return new Expense(data)
    },
    approveExpense: async (id: string) => {
        const { data } = await axios.patch(`/${MODEL}/${id}/approve`)
        return new Expense(data)
    },
    reviewExpense: async (id: string) => {
        const { data } = await axios.patch(`/${MODEL}/${id}/review`)
        return new Expense(data)
    },
    reviewMultipleExpenses: async (ids: string[]) => {
        const { data } = await axios.patch(`/${MODEL}/review`, { ids })
        return data
    },

    rejectExpense: async (id: string, rejectionNote: string) => {
        const { data } = await axios.patch(`/${MODEL}/${id}/reject`, { rejectionNote })
        return new Expense(data)
    },
    resetExpense: async (id: string) => {
        const { data } = await axios.patch(`/${MODEL}/${id}/reset`)
        return new Expense(data)
    },
    markExpenseAsPaid: async (id: string) => {
        const { data } = await axios.patch(`/${MODEL}/${id}/mark-as-paid`)
        return new Expense(data)
    },
    markMultipleExpensesAsPaid: async (ids: string[]) => {
        const { data } = await axios.patch(`/${MODEL}/mark-as-paid`, { ids })
        return data
    },
    markExpenseAsDone: async (id: string) => {
        const { data } = await axios.patch(`/${MODEL}/${id}/mark-as-done`)
        return new Expense(data)
    },
    markMultipleExpensesAsDone: async (ids: string[]) => {
        const { data } = await axios.patch(`/${MODEL}/mark-as-done`, { ids })
        return data
    },
    updateDocuments: async (id: string, kind: DocumentTypeEnum, documentData: any, operation: OperationTypeEnum) => {
        const { data } = await axios.put(`/${MODEL}/${id}/documents`, { kind, documentData, operation })
        return new Expense(data)
    },
    deleteExpense: async (id: string) => {
        const { data } = await axios.delete(`/${MODEL}/${id}`)
        return new Expense(data)
    },
    sendReminder: async (expenseId: string) => {
        return await axios.get(`${MODEL}/${expenseId}/remind`)
    },
    performMatching: (matchingData: MatchingDataInterface[]) => axios.post(`/matching/perform`, { matchingData: matchingData }),
    pauseSubscription: async (expenseId: string) => {
        const { data } = await axios.put(`${MODEL}/${expenseId}/pause`)
        return new Expense(data)
    },
    unpauseSubscription: async (expenseId: string) => {
        const { data } = await axios.put(`${MODEL}/${expenseId}/unpause`)
        return new Expense(data)
    },
    createComment: async (expenseId: string, comment: CommentItem) => {
        const { data } = await axios.post(`${MODEL}/${expenseId}/comments`, comment)
        return new Expense(data)
    },
    deleteComment: async (expenseId: string, comment: CommentItem) => {
        const { data } = await axios.delete(`${MODEL}/${expenseId}/comments/${comment.id}`)
        return new Expense(data)
    },
    updateComment: async (expenseId: string, comment: CommentItem) => {
        const { data } = await axios.patch(`${MODEL}/${expenseId}/comments/${comment.id}`, comment)
        return new Expense(data)
    },
    checkForDuplicate: async (duplicateData: any) => {
        const { data } = await axios.post(`${MODEL}/check-duplicate`, duplicateData)
        return data
    },
    getAdjacentExpenses: async (expenseId: string, table: any) => {
        const queryParams = await getTableQueryParams(table)
        const { data } = await axios.get(`${MODEL}/${expenseId}/adjacent?${queryParams.filterQueryString}${queryParams.searchQueryString}${queryParams.sortQueryString}`)
        return data
    },
    fetchPerDiemDestinationRates: async (year: number) => {
        const { data } = await axios.get(`/${MODEL}/perdiem/rates?year=${year}`)
        return data as PerDiemDestination[]
    },
    getNextExpenseNumber: async (): Promise<number | undefined> => {
        const { data } = await axios.get(`/${MODEL}/sequence/next-expense-number`)
        return data.expenseNumberSequence
    },
    exportAllSubscriptions: async (): Promise<void> => {
        await axios.post(`/${MODEL}/subscription/export`)
    },
}

export default ExpenseService
